/**
 * Attachment Service
 *
 * Handles upload, download, caching, and rekey of encrypted file attachments.
 * Works with attachment-crypto.js for encryption and the backend /api/docs/:id/blobs endpoints.
 */

import {
  encryptAttachment,
  decryptAttachment,
  rekeyBek,
  encryptFilename,
  decryptFilename,
  rekeyFilename,
} from '../crypto/attachment-crypto.js';
import * as StorageHealth from '../sync/storage-health.js';
import { VaultStorage } from '../crypto/vault-storage.js';
import { safeAttachmentMime } from './attachment-mime.js';
import { runOperation, beginOperation, notify } from '../host.js';
import { NOTICE } from '../notices.js';
import { uploadWithProgress, readResponseWithProgress } from '../../shared/utils/transfer-progress.js';
import { buildEnvelope, parseEnvelope, uint8ToBase64, base64ToUint8 } from './attachment-envelope.js';
export { uint8ToBase64, base64ToUint8 };
import { assertRoomFor, ensurePersistentStorage, makeRoomForOfflineCopy, makeRoomOnDevice } from './device-storage.js';
import { keepWaiting, readWaitingFile, waitingBytes, isWaiting, sendWaitingFiles } from './waiting-attachments.js';
import {
  putStoredAttachment,
  getStoredAttachment,
  deleteStoredAttachment,
  listStoredAttachments,
} from '../storage/index.js';
import { t } from '../../shared/i18n/index.js';
import { on, Events } from '../core/events.js';
import { isLinkMap, linkHeader } from '../sync/link-access.js';

/**
 * Headers for the attachments' routes: on a map open by its link, the link
 * (link-access.js), which the server checks instead of a membership.
 */
async function blobHeaders(docId) {
  const link = await linkHeader(docId);
  return { 'X-Requested-With': 'XMLHttpRequest', ...(link && { 'X-Orbital-Link': link }) };
}

/**
 * An online map's attachment that can't be shown now: the server can't be
 * reached and this device has no copy of it. The display shows "Needs a
 * connection" and retries when the connection returns.
 */
export function isNeedsConnection(error) {
  return error?.code === 'needs-connection';
}

/**
 * Call `callback` once the server may be reachable again: the browser's
 * `online` event, or the server indicator turning back to connected (the
 * network was up but the server was not, as on the desktop).
 * @returns {() => void} cancels
 */
export function whenConnectionReturns(callback) {
  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    cancel();
    callback();
  };
  // Connected, or sending: not "Connecting…", which also shows while the
  // server is still unreachable.
  const reachable = (status) => status?.level === 'ok'
    || status?.text === 'app.status.syncing' || status?.text === 'app.status.sendingFiles';
  const offStatus = on(Events.SERVER_STATUS_CHANGED, (status) => {
    if (reachable(status)) fire();
  });
  window.addEventListener('online', fire);
  function cancel() {
    offStatus();
    window.removeEventListener('online', fire);
  }
  return cancel;
}

function needsConnection() {
  const error = new Error(t('vault.device.needsConnection'));
  error.code = 'needs-connection';
  return error;
}

// Cache: docId/attachmentId → { objectUrl, mimeType }
const attachmentCache = new Map();

// Where a map is stored ("local" = on this device, "online"), injected at app
// boot (main.js). A map on this device keeps its attachments on the device
// (docs/plans/2026-09-29-attachments-move-with-their-map.md).
let attachmentStorageResolver = null;

export function setAttachmentStorageResolver(fn) {
  attachmentStorageResolver = typeof fn === 'function' ? fn : null;
}

/** Whether this map's attachments live on the device. */
export function attachmentsOnDevice(docId) {
  return attachmentStorageResolver?.(docId) === 'local';
}

// Resolver injected at app boot (main.js) so the editor's image NodeView can
// look up a doc's DEK without importing crdt-bridge directly (avoids a cycle:
// attachment-service ↔ crdt-bridge via share-revocation).
let attachmentDekResolver = null;

/**
 * Inject the DEK lookup. Call once at app boot with a (docId) → CryptoKey|null fn.
 */
export function setAttachmentDekResolver(fn) {
  attachmentDekResolver = typeof fn === 'function' ? fn : null;
}

/**
 * Seed the attachment cache with a pre-decrypted blob — used right after an
 * upload so the editor displays the original file instantly without a
 * decrypt-from-server round trip. Does NOT overwrite an existing entry.
 *
 * @param {string} docId
 * @param {string} attachmentId
 * @param {{ blob: Blob, mimeType: string, filename?: string }} entry
 */
export function populateAttachmentCache(docId, attachmentId, { blob, mimeType, filename = '' }) {
  const cacheKey = `${docId}/${attachmentId}`;
  if (attachmentCache.has(cacheKey)) return;
  blob = blob.slice(0, blob.size, safeAttachmentMime(mimeType));
  const objectUrl = URL.createObjectURL(blob);
  attachmentCache.set(cacheKey, { objectUrl, blob, filename, mimeType });
}

/**
 * Look up a doc's DEK via the injected resolver. Returns null if no resolver
 * is wired or the doc isn't loaded. Same lookup `resolveAttachmentForDisplay`
 * uses internally — exposed so cross-doc paste rekey can reach source DEKs
 * without re-importing crdt-bridge.
 *
 * @param {string} docId
 * @returns {CryptoKey|null}
 */
export function getAttachmentDek(docId) {
  return attachmentDekResolver?.(docId) ?? deviceKeys.get(docId) ?? null;
}

// Keys of maps on this device, loaded once (they are not in the sync resolver).
const deviceKeys = new Map();

// Async DEK loader injected at app boot. Covers docs that aren't currently
// loaded in memory (the resolver only sees active + linked + shared-in-sync
// docs). Cross-doc paste hits this for source docs the user has in their
// vault but isn't currently viewing.
let attachmentDekLoader = null;

/**
 * Inject the async DEK loader. Call once at app boot with a (docId) →
 * Promise<CryptoKey|null> fn. Typically wraps VaultStorage.getDocDEK +
 * crypto.subtle.importKey('raw', bytes, 'AES-GCM').
 */
export function setAttachmentDekLoader(fn) {
  attachmentDekLoader = typeof fn === 'function' ? fn : null;
}

/**
 * Async DEK lookup. Hits the sync resolver first (in-memory: active doc,
 * linked-doc registry, shared sync managers); falls back to the loader
 * (vault unwrap → importKey) for any other doc the user has access to.
 * Returns null if neither path produces a key.
 *
 * Used by cross-doc paste relink so a source doc that's in the user's vault
 * but not currently open can still have its BEK unwrapped.
 *
 * @param {string} docId
 * @returns {Promise<CryptoKey|null>}
 */
export async function loadAttachmentDek(docId, { create = false } = {}) {
  const cached = getAttachmentDek(docId);
  if (cached) return cached;
  if (attachmentDekLoader) {
    try {
      // `create`: a map on this device gets its key at its first attachment.
      const key = await attachmentDekLoader(docId, { create });
      if (key && attachmentsOnDevice(docId)) deviceKeys.set(docId, key);
      return key;
    } catch { return null; }
  }
  return null;
}

/**
 * Load the key of a map stored on this device before its views draw, so its
 * attachments display and can be added (it gets a key the first time). No-op
 * for online maps. A failure leaves the map usable without attachments.
 */
export async function prepareDeviceMapKey(docId) {
  if (!docId || !attachmentsOnDevice(docId)) return;
  // A map on this device exists nowhere else: ask the browser to keep it
  // (the answer drives the "may be deleted" warnings).
  void ensurePersistentStorage();
  try {
    await loadAttachmentDek(docId, { create: true });
  } catch (err) {
    console.warn('[Attachments] Key for a map on this device unavailable:', err);
  }
}

/** An attachment's reference in a note: "attachment:MAP/FILE". */
export function attachmentRef(docId, attachmentId) {
  return `attachment:${docId}/${attachmentId}`;
}

/** The map and file of an attachment reference, or null. */
export function parseAttachmentRef(ref) {
  const match = /^attachment:([a-f0-9-]+)\/([a-f0-9-]+)$/.exec(typeof ref === 'string' ? ref : '');
  return match ? { docId: match[1], attachmentId: match[2] } : null;
}

/**
 * Resolve an `attachment:DOC/BLOB` reference to a displayable object URL.
 * Hits the in-memory cache first (instant for freshly-uploaded blobs);
 * otherwise downloads + decrypts via the resolver-provided DEK.
 *
 * The key is the one of the map in the reference, which may not be the open
 * one (a note copied from another map): read from the vault when that map is
 * not loaded.
 *
 * Returns null if no DEK is available (no resolver wired, or doc not loaded).
 * Throws if download/decrypt fails so callers can show a broken-image
 * indicator on rejection.
 *
 * @param {string} docId
 * @param {string} attachmentId
 * @param {{name?: string}} [options] name: the file's name in the note (for an error)
 * @returns {Promise<string|null>}
 */
export async function resolveAttachmentForDisplay(docId, attachmentId, { name } = {}) {
  const cacheKey = `${docId}/${attachmentId}`;
  const cached = attachmentCache.get(cacheKey);
  if (cached) return cached.objectUrl;
  const dek = getAttachmentDek(docId) || await loadAttachmentDek(docId);
  if (!dek) return null;
  const { objectUrl } = await getAttachmentUrl(docId, attachmentId, dek, { name });
  return objectUrl;
}

// Allowed file extensions (client-side validation before upload)
const ALLOWED_EXTENSIONS = new Set([
  // Images
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
  // Audio
  'mp3', 'wav', 'ogg', 'm4a', 'webm', 'flac',
  // Documents
  'pdf', 'md', 'txt', 'csv', 'json',
  // Office
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
]);

const MIME_MAP = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4',
  webm: 'audio/webm', flac: 'audio/flac',
  pdf: 'application/pdf', md: 'text/markdown', txt: 'text/plain',
  csv: 'text/csv', json: 'application/json',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

/**
 * Validate a file before upload.
 * @param {File} file
 * @returns {{ valid: boolean, error?: string, ext?: string, mimeType?: string }}
 */
export function validateFile(file) {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !ALLOWED_EXTENSIONS.has(ext)) {
    return { valid: false, error: t('vault.attachment.typeUnsupported', { ext: ext || '?' }) };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: t('vault.attachment.tooLarge', { size: MAX_FILE_SIZE / 1024 / 1024 }) };
  }
  const mimeType = MIME_MAP[ext] || file.type || 'application/octet-stream';
  return { valid: true, ext, mimeType };
}

/**
 * Upload a file as an encrypted attachment.
 *
 * Retries once on a 409 `dek_stale` response, which the server returns when
 * a DEK rotation lands between the client wrapping the BEK and the server
 * inserting the `doc_blob_refs` row. Without this retry, the row would land
 * with a wrapping under the (now-zeroed) old DEK and the attachment would
 * be permanently undecryptable.
 *
 * @param {string} docId - Document ID
 * @param {File} file - The file to upload
 * @param {CryptoKey} dek - Document encryption key (AES-GCM, for wrapping BEK)
 * @returns {Promise<{ attachmentId: string, filename: string, mimeType: string }>}
 */
export async function uploadAttachment(docId, file, dek, { onProgress } = {}) {
  if (onProgress) return uploadAttachmentImpl(docId, file, dek, onProgress);
  return runOperation(t('vault.attachment.uploadTitle', { name: file.name }), progress => uploadAttachmentImpl(docId, file, dek, progress.update),
    (result) => [result?.waiting ? t('vault.device.waitingKept') : t('vault.attachment.uploaded')]);
}

async function uploadAttachmentImpl(docId, file, dek, report) {
  StorageHealth.assertHealthy();
  // Open by its link, not as a member: files are viewed, not added
  // (docs/plans/2026-10-28-link-sharing.md, choice 5).
  if (isLinkMap(docId)) throw new Error(t('share.link.noFiles'));
  const validation = validateFile(file);
  if (!validation.valid) throw new Error(validation.error);

  report({ label: t('vault.attachment.reading') });
  const plaintext = await file.arrayBuffer();
  if (attachmentsOnDevice(docId)) return storeOnDevice(docId, file, validation.mimeType, plaintext, dek, report);
  // The client chooses the ID (the server refuses a taken one), so a file
  // keeps it when its map moves between the device and the server, or waits
  // on the device to be sent.
  const blobId = crypto.randomUUID();

  // Encrypt with the map's key; `fingerprint` names that key's wrapping so
  // the server can refuse a file wrapped for a key replaced since.
  const seal = async (currentDek) => {
    report({ label: t('vault.attachment.encrypting') });
    const { encrypted, wrappedBek, bekRaw } = await encryptAttachment(currentDek, plaintext);
    const encryptedFilename = await encryptFilename(currentDek, file.name);
    const fingerprint = await VaultStorage.getDocDEKFingerprint(docId);
    return { encrypted, wrappedBek, bekRaw, encryptedFilename, fingerprint };
  };

  // POST it. Returns parsed JSON on success, throws { stale: true } on 409
  // dek_stale (so the outer logic can refresh + retry), an error with
  // `network: true` when the server can't be reached, Error otherwise.
  const post = async (sealed) => {
    const formData = new FormData();
    formData.append('file', new Blob([sealed.encrypted]), 'blob');
    formData.append('metadata', JSON.stringify({
      mime_type: validation.mimeType,
      size_original: file.size,
      wrapped_bek: uint8ToBase64(sealed.wrappedBek),
      filename_encrypted: uint8ToBase64(sealed.encryptedFilename),
      dek_fingerprint: sealed.fingerprint,
      blob_id: blobId,
    }));

    report({ label: t('vault.transfer.uploading') });
    const response = await uploadWithProgress(`/api/docs/${docId}/blobs`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }, report);

    if (response.status === 409) {
      const body = await response.json().catch(() => ({}));
      if (body?.code === 'dek_stale') {
        const err = new Error('dek_stale');
        err.stale = true;
        throw err;
      }
      throw new Error(body?.error || t('vault.attachment.uploadConflict'));
    }
    if ([502, 503, 504].includes(response.status)) {
      const err = new Error(t('vault.attachment.uploadFailedStatus', { status: response.status }));
      err.network = true;
      throw err;
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: t('vault.attachment.uploadFailed') }));
      throw new Error(err.error || t('vault.attachment.uploadFailedStatus', { status: response.status }));
    }
    return response.json();
  };

  let sealed = await seal(dek);
  // The server can't be reached: the file waits on this device.
  const wait = () => keepUntilSent(docId, blobId, sealed, file, validation.mimeType, report);
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return wait();

  let result;
  try {
    result = await post(sealed);
  } catch (e) {
    if (e?.network) return wait();
    if (!e?.stale) throw e;
    report({ label: t('vault.attachment.verifyingKey') });
    // DEK rotated mid-upload. Pull the fresh wrapping from the server,
    // re-unwrap, and retry exactly once. A second 409 means rotations are
    // arriving faster than we can keep up — bubble up so the caller can
    // surface it.
    const newDekBytes = await VaultStorage.refreshDocDek(docId);
    if (!newDekBytes) throw new Error(t('vault.attachment.keyRefreshFailed'));
    const newDek = await crypto.subtle.importKey(
      'raw', newDekBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'],
    );
    sealed = await seal(newDek);
    try {
      result = await post(sealed);
    } catch (again) {
      if (again?.network) return wait();
      throw again;
    }
  }

  // The server has it; keep a copy for offline use too (best effort).
  void keepOfflineCopy(docId, blobId, buildEnvelope({
    wrapped_bek: uint8ToBase64(sealed.wrappedBek),
    filename_encrypted: uint8ToBase64(sealed.encryptedFilename),
    mime_type: validation.mimeType,
    size_original: file.size,
  }, sealed.encrypted));

  return {
    attachmentId: result.blob_id,
    filename: file.name,
    mimeType: validation.mimeType,
  };
}

/**
 * A map on this device: encrypt exactly as for the server and keep the file
 * on the device, in the server's download format.
 */
/**
 * An online map's file that could not reach the server: kept on this device
 * until the connection returns (waiting-attachments.js). It exists nowhere
 * else, so it takes room from offline copies, never the reverse, and is
 * refused with the reason when there is no room.
 */
async function keepUntilSent(docId, blobId, sealed, file, mimeType, report) {
  report({ label: t('vault.device.saving') });
  const room = await makeRoomOnDevice(docId, sealed.encrypted.length + 4096, { waitingBytes: waitingBytes(docId) });
  if (room) {
    const error = new Error(room.text);
    error.code = room.reason;
    throw error;
  }
  // It exists nowhere else: ask the browser to keep it, without waiting
  // (ensurePersistentStorage: Firefox answers only once the user does).
  void ensurePersistentStorage();
  await keepWaiting(docId, blobId, {
    ...sealed,
    filename: file.name,
    mimeType,
    size: file.size,
  });
  return { attachmentId: blobId, filename: file.name, mimeType, waiting: true };
}

async function storeOnDevice(docId, file, mimeType, plaintext, dek, report) {
  report({ label: t('vault.attachment.encrypting') });
  const { encrypted, wrappedBek } = await encryptAttachment(dek, plaintext);
  const encryptedFilename = await encryptFilename(dek, file.name);
  const envelope = buildEnvelope({
    wrapped_bek: uint8ToBase64(wrappedBek),
    filename_encrypted: uint8ToBase64(encryptedFilename),
    mime_type: mimeType,
    size_original: file.size,
    created_at: new Date().toISOString(),
  }, encrypted);
  await assertRoomFor('device', docId, envelope.length);
  // It exists nowhere else: ask the browser to keep it, without waiting
  // (ensurePersistentStorage: Firefox answers only once the user does).
  void ensurePersistentStorage();
  report({ label: t('vault.device.saving') });
  const attachmentId = crypto.randomUUID();
  await putStoredAttachment('device', docId, attachmentId, envelope);
  return { attachmentId, filename: file.name, mimeType };
}

/**
 * Download and decrypt an attachment, returning an object URL.
 * Results are cached per-session to avoid re-fetching.
 *
 * Without `onProgress`, a card shows the download; a failure is a notice
 * (attachment-failed) with the map's and the file's names and IDs, shown by
 * the interface (interfaces/classic/ui/attachment-failures.js).
 *
 * @param {string} docId - the map the file belongs to (from its reference)
 * @param {string} attachmentId
 * @param {CryptoKey|null} dek - that map's key; null: read from the vault
 * @param {{onProgress?: Function, name?: string}} [options] name: the file's
 *   name in the note, for an error (a file that can't be decrypted has no
 *   readable stored name)
 * @returns {Promise<{ objectUrl: string, blob: Blob, filename: string, mimeType: string }>}
 */
export async function getAttachmentUrl(docId, attachmentId, dek, { onProgress, name } = {}) {
  const cacheKey = `${docId}/${attachmentId}`;
  if (attachmentCache.has(cacheKey)) {
    return attachmentCache.get(cacheKey);
  }
  if (onProgress) return downloadAttachment(docId, attachmentId, dek || await requireKey(docId), onProgress);
  const progress = beginOperation(t('vault.attachment.downloadTitle'));
  try {
    await progress.paint();
    const result = await downloadAttachment(docId, attachmentId, dek || await requireKey(docId), progress.update);
    progress.finish(t('vault.attachment.ready'));
    return result;
  } catch (error) {
    progress.dismiss();
    // The attachment's place in the note says it needs a connection.
    if (!isNeedsConnection(error)) {
      notify({ code: NOTICE.ATTACHMENT_FAILED, level: "error", data: { docId, attachmentId, name, error } });
    }
    throw error;
  }
}

/** A map's key from the vault; an error when this account has none (a map it cannot open). */
async function requireKey(docId) {
  const key = await loadAttachmentDek(docId);
  if (key) return key;
  const error = new Error(t('vault.attachment.failures.noKey'));
  error.code = 'no-key';
  throw error;
}

async function downloadAttachment(docId, attachmentId, dek, report) {
  const cacheKey = `${docId}/${attachmentId}`;
  let buffer;
  let fromCopy = false;
  if (attachmentsOnDevice(docId)) {
    buffer = await getStoredAttachment('device', docId, attachmentId);
    if (!buffer) throw new Error(t('vault.device.fileMissing'));
  } else {
    ({ buffer, fromCopy } = await downloadOnline(docId, attachmentId, report));
  }

  // 4-byte header length + JSON header + encrypted bytes (attachment-envelope.js)
  report({ label: t('vault.attachment.decrypting') });
  const { header, encrypted: encryptedBytes } = parseEnvelope(buffer);

  // Decode wrapped BEK and decrypt
  const wrappedBek = base64ToUint8(header.wrapped_bek);
  let decrypted;
  try {
    decrypted = await decryptAttachment(dek, encryptedBytes, wrappedBek);
  } catch (error) {
    // An offline copy wrapped for the map's previous key (a member was
    // removed since): drop it; the server has the rewrapped file.
    if (!fromCopy) throw error;
    await deleteStoredAttachment('offline', docId, attachmentId).catch(() => {});
    throw needsConnection();
  }

  // Create object URL
  // MIME metadata is not authenticated with the encrypted attachment bytes.
  const safeMime = safeAttachmentMime(header.mime_type);
  const blob = new Blob([decrypted], { type: safeMime });
  const objectUrl = URL.createObjectURL(blob);

  // Decrypt the filename (ciphertext is stored on the server; plaintext never
  // leaves the client). Legacy blobs uploaded before filename encryption have
  // no stored ciphertext — fall back to a placeholder rather than showing
  // something misleading.
  const encryptedFilename = header.filename_encrypted
    ? base64ToUint8(header.filename_encrypted)
    : null;
  const decryptedName = await decryptFilename(dek, encryptedFilename);

  const result = {
    objectUrl,
    blob,
    filename: decryptedName ?? '',
    mimeType: header.mime_type,
  };

  attachmentCache.set(cacheKey, result);
  return result;
}

/**
 * An online map's attachment: from the server, keeping an offline copy on
 * this device; from that copy when the server can't be reached. A refusal
 * by the server (no access, not found) is an error, never a reason to show
 * a copy.
 * @returns {Promise<{buffer: ArrayBuffer|Uint8Array, fromCopy: boolean}>}
 */
async function downloadOnline(docId, attachmentId, report) {
  // Attached while offline and not sent yet: only this device has it.
  const waiting = await readWaitingFile(docId, attachmentId).catch(() => null);
  if (waiting) return { buffer: waiting, fromCopy: false };
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!offline) {
    let response = null;
    try {
      report({ label: t('vault.attachment.connecting') });
      response = await fetch(`/api/docs/${docId}/blobs/${attachmentId}`, {
        credentials: 'include',
        headers: await blobHeaders(docId),
      });
    } catch { /* network error: try the copy */ }
    if (response?.ok) {
      const buffer = await readResponseWithProgress(response, report, MAX_FILE_SIZE + 1024 * 1024);
      void keepOfflineCopy(docId, attachmentId, buffer);
      return { buffer, fromCopy: false };
    }
    if (response && ![502, 503, 504].includes(response.status)) {
      // Gone, or no longer shared with this account: its copy goes too.
      if (response.status === 403 || response.status === 404) {
        void deleteStoredAttachment('offline', docId, attachmentId).catch(() => {});
      }
      const error = new Error(t('vault.attachment.downloadFailedStatus', { status: response.status }));
      error.status = response.status;
      throw error;
    }
  }
  const copy = await getStoredAttachment('offline', docId, attachmentId).catch(() => null);
  if (copy) return { buffer: copy, fromCopy: true };
  throw needsConnection();
}

/** Keep a downloaded file for offline use, if there is room (best effort). */
async function keepOfflineCopy(docId, attachmentId, buffer, { evict = true } = {}) {
  try {
    const bytes = new Uint8Array(buffer);
    if (await makeRoomForOfflineCopy(docId, bytes.length, { evict, waitingBytes: waitingBytes(docId) })) {
      await putStoredAttachment('offline', docId, attachmentId, bytes);
      return true;
    }
  } catch (error) {
    console.warn('[Attachments] Offline copy not kept:', error?.message || error);
  }
  return false;
}

/**
 * Copy an online map's attachments to this device in the background, so
 * they show offline too: files not copied yet, one at a time, and only into
 * free room (never pushing out other copies). Skipped on a connection that
 * asks to save data. Stops when `stillWanted()` turns false (another map
 * opened, signed out) or the server can't be reached.
 *
 * @param {string} docId
 * @param {string[]} attachmentIds the files the map refers to
 * @returns {Promise<number>} how many files were copied
 */
export async function copyAttachmentsForOffline(docId, attachmentIds, { stillWanted = () => true } = {}) {
  if (attachmentsOnDevice(docId) || !attachmentIds.length) return 0;
  if (typeof navigator !== 'undefined' && (navigator.onLine === false || navigator.connection?.saveData)) return 0;
  const copied = new Set((await listStoredAttachments('offline', docId).catch(() => [])).map((file) => file.id));
  const wanted = attachmentIds.filter((id) => !copied.has(id) && !isWaiting(docId, id));
  if (!wanted.length) return 0;
  // The server lists the map's files with their sizes: ask for room first.
  let sizes;
  try {
    const response = await fetch(`/api/docs/${docId}/blobs`, {
      credentials: 'include',
      headers: await blobHeaders(docId),
    });
    if (!response.ok) return 0;
    sizes = new Map(((await response.json()).blobs || []).map((blob) => [blob.blob_id, blob.size_original || 0]));
  } catch {
    return 0;
  }
  let count = 0;
  for (const id of wanted) {
    if (!stillWanted()) break;
    if (!sizes.has(id)) continue; // another map's file, or gone
    // The view downloaded it meanwhile: it kept the copy.
    if (attachmentCache.has(`${docId}/${id}`)) continue;
    // Encryption and the header add a little to the file's size.
    const room = await makeRoomOnDevice(docId, sizes.get(id) + 4096, { evict: false, waitingBytes: waitingBytes(docId) });
    if (room) break;
    let response;
    try {
      response = await fetch(`/api/docs/${docId}/blobs/${id}`, {
        credentials: 'include',
        headers: await blobHeaders(docId),
      });
    } catch {
      break;
    }
    if (!response.ok) {
      if ([502, 503, 504].includes(response.status)) break;
      continue;
    }
    const buffer = await readResponseWithProgress(response, () => {}, MAX_FILE_SIZE + 1024 * 1024);
    if (!(await keepOfflineCopy(docId, id, buffer, { evict: false }))) break;
    count++;
  }
  return count;
}

/**
 * Send the files attached while offline that the server can take now
 * (waiting-attachments.js): only for online maps of this account that it
 * may edit.
 */
export function sendWaitingAttachments() {
  return sendWaitingFiles({
    loadKey: (docId) => loadAttachmentDek(docId),
    refreshKey: async (docId) => {
      const bytes = await VaultStorage.refreshDocDek(docId);
      return bytes ? crypto.subtle.importKey('raw', bytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']) : null;
    },
    fingerprintOf: (docId) => VaultStorage.getDocDEKFingerprint(docId),
    canSend: (docId) => {
      const doc = VaultStorage.getDoc?.(docId);
      return !!doc && doc._storage !== 'local' && doc.role !== 'viewer';
    },
  });
}

/**
 * Walk a tree (+ optional doc-level notes) and collect every blob id
 * referenced by an `attachment:<docId>/<blobId>` URI in any notes string.
 * Returns the set of blob-id UUIDs (second group of each match), independent
 * of the doc-id namespace — the server scopes deletion to the URL's docId,
 * so extra ids from cross-doc refs are harmless.
 *
 * @param {Object|null} tree - Root tree node with { notes, children, ... }.
 * @param {Array|null}  docNotes - Optional doc-level notes array.
 * @returns {string[]}
 */
export function collectAttachmentRefsFromTree(tree, docNotes = null, routineEvents = []) {
  const ids = new Set();
  // Loose pattern matches the convention used elsewhere in the codebase
  // (md-export-import.js, attachment.js, image-extension.js). A stricter
  // {36}-char match could miss a legitimate reference whose blob id
  // deviates from the expected shape, and sweep would then delete the
  // real blob — better to over-collect into the keep set (server scopes
  // deletion to this doc, so stray ids are harmless).
  const pattern = /attachment:[a-f0-9-]+\/([a-f0-9-]+)/gi;
  const scan = (str) => {
    if (typeof str !== 'string' || !str.includes('attachment:')) return;
    for (const m of str.matchAll(pattern)) ids.add(m[1].toLowerCase());
  };
  const visit = (node) => {
    if (!node) return;
    scan(node.notes);
    (node.children || []).forEach(visit);
  };
  visit(tree);
  (docNotes || []).forEach((n) => scan(n?.content));
  for (const event of routineEvents) for (const step of event.steps || []) scan(step.notes);
  return [...ids];
}

/**
 * Ask the server to delete every blob in this doc that's not in `keepIds`.
 * Intended to run at doc-close: the client walked the tree, built the set of
 * referenced blobs, and the server prunes the rest. Orphans freed here
 * release their quota.
 *
 * The server enforces write-role authz and skips blobs created within its
 * grace window (to protect just-uploaded blobs that haven't been linked into
 * the tree yet).
 *
 * @param {string}   docId
 * @param {string[]} keepIds
 * @returns {Promise<{ deleted: number, kept: number } | null>}
 */
export async function sweepAttachments(docId, keepIds) {
  // Files of a map on this device are removed only by the user (plan, point 8).
  if (attachmentsOnDevice(docId)) return { deleted: 0 };
  const response = await fetch(`/api/docs/${docId}/blobs/sweep`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ keep: keepIds }),
  });
  if (!response.ok) {
    // Viewer role / offline / other — don't treat as fatal, caller is
    // "best-effort GC on close".
    return null;
  }
  return response.json().catch(() => null);
}

/**
 * Cross-doc paste: register `doc_blob_refs` rows in `destDocId` for blobs
 * that currently live in `sourceDocId`. The caller has already unwrapped each
 * BEK with the source DEK and rewrapped it under the destination DEK (same
 * shape as the recovery import flow, but with regular-access authz on the
 * server — no recovery claim required).
 *
 * Server inserts rows with ON CONFLICT DO NOTHING, so re-running for blobs
 * already linked is a safe idempotent no-op.
 *
 * @param {string} destDocId
 * @param {string} sourceDocId
 * @param {Array<{ blob_id: string, wrapped_bek: string, filename_encrypted?: string|null }>} items
 * @returns {Promise<{ imported: number } | null>}
 */
export async function linkBlobRefs(destDocId, sourceDocId, items) {
  if (!items || items.length === 0) return { imported: 0 };
  const dek_fingerprint = await VaultStorage.getDocDEKFingerprint(destDocId);
  const response = await fetch(`/api/docs/${destDocId}/blob-refs/link`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ source_doc_id: sourceDocId, items, dek_fingerprint }),
  });
  // Surface the dek_stale signal so the caller can refresh and retry.
  // Returning null for it would lose the distinction between "rotation
  // race, retryable" and "server error, give up".
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    if (body?.code === 'dek_stale') {
      return { stale: true };
    }
    return null;
  }
  if (!response.ok) return null;
  return response.json().catch(() => null);
}

/**
 * Delete an attachment.
 * @param {string} docId
 * @param {string} attachmentId
 */
export async function deleteAttachment(docId, attachmentId) {
  if (attachmentsOnDevice(docId)) {
    await deleteStoredAttachment('device', docId, attachmentId);
  } else {
    const response = await fetch(`/api/docs/${docId}/blobs/${attachmentId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) {
      throw new Error(t('vault.attachment.deleteFailedStatus', { status: response.status }));
    }
  }

  // Revoke cached URL if any
  const cacheKey = `${docId}/${attachmentId}`;
  const cached = attachmentCache.get(cacheKey);
  if (cached) {
    URL.revokeObjectURL(cached.objectUrl);
    attachmentCache.delete(cacheKey);
  }
}

/**
 * List all attachments for a document. Filenames are decrypted client-side
 * using the provided DEK (pass null to skip decryption — filename will be '').
 *
 * @param {string} docId
 * @param {CryptoKey|null} [dek] - Doc encryption key for filename decryption.
 * @returns {Promise<Array<{ attachmentId, filename, filenameEncrypted, mimeType, sizeOriginal, wrappedBek, createdAt }>>}
 */
export async function listAttachments(docId, dek = null) {
  if (attachmentsOnDevice(docId)) {
    const stored = await listStoredAttachments('device', docId);
    return Promise.all(stored.filter((file) => file.header).map(async ({ id, header }) => {
      const encryptedName = header.filename_encrypted ? base64ToUint8(header.filename_encrypted) : null;
      const filename = dek && encryptedName ? (await decryptFilename(dek, encryptedName)) ?? '' : '';
      return {
        attachmentId: id,
        filename,
        filenameEncrypted: header.filename_encrypted || null,
        mimeType: header.mime_type,
        sizeOriginal: header.size_original,
        wrappedBek: header.wrapped_bek,
        createdAt: header.created_at || null,
      };
    }));
  }
  const response = await fetch(`/api/docs/${docId}/blobs`, {
    credentials: 'include',
    headers: await blobHeaders(docId),
  });

  if (!response.ok) {
    throw new Error(`Failed to list attachments (${response.status})`);
  }

  const data = await response.json();
  return Promise.all(data.blobs.map(async (b) => {
    let filename = '';
    const encryptedFilenameBytes = b.filename_encrypted
      ? base64ToUint8(b.filename_encrypted)
      : null;
    if (dek && encryptedFilenameBytes) {
      filename = (await decryptFilename(dek, encryptedFilenameBytes)) ?? '';
    }
    return {
      attachmentId: b.blob_id,
      filename,
      filenameEncrypted: b.filename_encrypted || null,
      mimeType: b.mime_type,
      sizeOriginal: b.size_original,
      wrappedBek: b.wrapped_bek,
      createdAt: b.created_at,
    };
  }));
}

/**
 * Re-key all attachments for a document after DEK rotation.
 * Unwraps each BEK with oldDek, re-wraps with newDek, uploads to server.
 *
 * @param {string} docId
 * @param {CryptoKey} oldDek
 * @param {CryptoKey} newDek
 */
export async function rekeyDocAttachments(docId, oldDek, newDek) {
  const attachments = await listAttachments(docId);
  if (attachments.length === 0) return;

  const rekeyItems = [];
  for (const attachment of attachments) {
    const oldWrapped = base64ToUint8(attachment.wrappedBek);
    const newWrapped = await rekeyBek(oldDek, newDek, oldWrapped);
    // Re-encrypt the filename too (stored server-side encrypted under the
    // doc DEK — must follow every rotation). Legacy blobs without a stored
    // filename get null here, and the server update leaves the column
    // untouched.
    let newFilenameEncryptedB64 = null;
    if (attachment.filenameEncrypted) {
      const newFilename = await rekeyFilename(
        oldDek,
        newDek,
        base64ToUint8(attachment.filenameEncrypted),
      );
      if (newFilename) {
        newFilenameEncryptedB64 = uint8ToBase64(newFilename);
      }
    }
    rekeyItems.push({
      blob_id: attachment.attachmentId,
      wrapped_bek: uint8ToBase64(newWrapped),
      filename_encrypted: newFilenameEncryptedB64,
    });
  }

  const response = await fetch(`/api/docs/${docId}/blobs/rekey`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ blobs: rekeyItems }),
  });

  if (!response.ok) {
    throw new Error(`Failed to rekey attachments (${response.status})`);
  }

  // Clear cache since wrapped BEKs changed
  clearAttachmentCache();
}

/**
 * Revoke all cached object URLs. Call on doc switch or logout.
 */
export function clearAttachmentCache() {
  for (const cached of attachmentCache.values()) {
    URL.revokeObjectURL(cached.objectUrl);
  }
  attachmentCache.clear();
  deviceKeys.clear();
}
